import { ChevronDown, Plus, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";
import { SearchField } from "./field";
import { SelectControl } from "./select";

export function Toolbar({
  title,
  actionLabel,
  onAction,
  actions,
  children
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="ui-toolbar">
      <div className="ui-toolbar-head">
        <h1>{title}</h1>
        {actions}
        {actionLabel && onAction && (
          <Button variant="primary" size="compact" type="button" onClick={onAction}>
            <Plus size={18} /> {actionLabel}
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

export function ToolbarFilters({ activeCount = 0, children }: { activeCount?: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const contentID = useId();

  return (
    <div className="ui-toolbar-filters" data-open={open ? "true" : undefined}>
      <button
        className="ui-toolbar-filter-trigger"
        type="button"
        aria-controls={contentID}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <SlidersHorizontal size={17} aria-hidden="true" />
        <span>Filters</span>
        {activeCount > 0 ? <span className="ui-toolbar-filter-count">{activeCount}</span> : null}
        <ChevronDown className="ui-toolbar-filter-chevron" size={17} aria-hidden="true" />
      </button>
      <div className="ui-toolbar-filter-grid" id={contentID}>{children}</div>
    </div>
  );
}

export interface AppliedFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

export function AppliedFilters({
  filters,
  onClear
}: {
  filters: AppliedFilter[];
  onClear?: () => void;
}) {
  if (filters.length === 0) {
    return null;
  }

  return (
    <div className="ui-applied-filters" aria-label="Applied filters">
      <span>Applied</span>
      {filters.map((filter) => (
        <Button
          className="ui-button-applied-filter"
          key={filter.key}
          variant="secondary"
          size="compact"
          type="button"
          onClick={filter.onRemove}
          aria-label={`Remove ${filter.label} filter`}
        >
          {filter.label}
          <X size={14} aria-hidden="true" />
        </Button>
      ))}
      {filters.length > 1 && onClear ? (
        <Button className="ui-button-clear-filters" variant="ghost" size="compact" type="button" onClick={onClear}>
          Clear all
        </Button>
      ) : null}
    </div>
  );
}

export function ChoiceGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ui-choice-group" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function ToolbarSelect<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: Array<{ key: T; label: string }>;
  onChange: (value: T) => void;
}) {
  const labelID = useId();

  return (
    <div className="ui-toolbar-select">
      <span id={labelID}>{label}</span>
      <SelectControl
        value={value}
        aria-labelledby={labelID}
        onValueChange={(nextValue) => onChange(nextValue as T)}
        options={options.map((option) => ({ value: option.key, label: option.label }))}
      />
    </div>
  );
}

export function ToolbarSearch({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return <SearchField label={label} value={value} onChange={onChange} placeholder={placeholder} />;
}

export function SegmentedControl<T extends string>({
  className,
  label,
  value,
  options,
  onChange
}: {
  className?: string;
  label: string;
  value: T;
  options: Array<{ key: T; label: string }>;
  onChange: (value: T) => void;
}) {
  const name = useId();
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const selectedOptionRef = useRef<HTMLLabelElement | null>(null);

  useEffect(() => {
    const tabs = tabsRef.current;
    const selectedOption = selectedOptionRef.current;
    if (!tabs || !selectedOption || tabs.offsetParent === null) {
      return;
    }
    const optionStart = selectedOption.offsetLeft;
    const optionEnd = optionStart + selectedOption.offsetWidth;
    const visibleStart = tabs.scrollLeft;
    const visibleEnd = visibleStart + tabs.clientWidth;
    if (optionStart < visibleStart) {
      tabs.scrollTo({ left: optionStart, behavior: "auto" });
    } else if (optionEnd > visibleEnd) {
      tabs.scrollTo({ left: optionEnd - tabs.clientWidth, behavior: "auto" });
    }
  }, [value]);

  return (
    <div className={cn("ui-segmented-control", className)}>
      <label className="ui-segmented-select">
        <span>{label}</span>
        <SelectControl
          aria-label={label}
          value={value}
          onValueChange={(nextValue) => onChange(nextValue as T)}
          options={options.map((option) => ({ label: option.label, value: option.key }))}
        />
      </label>
      <div ref={tabsRef} className="ui-filter-tabs" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <label
            className="ui-segmented-option"
            key={option.key}
            ref={option.key === value ? selectedOptionRef : undefined}
          >
            <input
              className="sr-only"
              type="radio"
              name={name}
              value={option.key}
              checked={value === option.key}
              onChange={() => onChange(option.key)}
            />
            <span className="ui-tabs-trigger">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
